import {
  Users, Zap, Rocket, GraduationCap, Building2, Video, ListChecks, MessageSquare, Bell,
  FileText, ClipboardCheck, Trophy, Gavel, Tags, Layers, LayoutDashboard, KeyRound,
  Briefcase, Share2, Eye, MapPin, Presentation, UserCog, Timer,
} from "lucide-react";
import type { DetailContent } from "./types";

export const solutions: DetailContent[] = [
  {
    slug: "hr-for-startups",
    family: "solutions",
    icon: Users,
    name: "HR for Startups",
    keyword: "HR for startups",
    summary: "Hire your first ten on evidence you can watch.",
    metaTitle: "HR for Startups: Video-First Hiring | Donjo",
    metaDescription:
      "HR for startups without the CV pile. Post a role, get video proof of skill, shortlist and message candidates in one place. Start free on Donjo.",
    eyebrow: "Solution",
    headline: "Hire your first ten on proof, not CVs.",
    subhead: "HR for startups: candidates answer your question on video, and you shortlist before any interview.",
    glance: {
      title: "You get",
      items: ["Job posts with a video prompt", "One queue for every applicant", "Shortlists, notes and messaging"],
    },
    problem: {
      title: "A bad early hire costs runway",
      points: ["CVs describe the past, not the person", "Screening calls eat founder time", "Unconventional talent gets filtered out"],
    },
    help: {
      title: "Proof first, conversation second",
      intro: "Ask a real question on video. Watch answers, shortlist, then talk.",
      pillars: [
        { title: "Ask", body: "Attach a video prompt to any role." },
        { title: "Review", body: "Every applicant and clip in one view." },
        { title: "Decide", body: "Shortlist, reject and message in-app." },
      ],
    },
    capabilitiesTitle: "Everything a lean team needs",
    capabilities: [
      { icon: Briefcase, title: "Job postings", body: "Skills, level, deadline and a video question." },
      { icon: Video, title: "Video applications", body: "Applicants record or upload a clip." },
      { icon: ListChecks, title: "Status pipeline", body: "Pending, reviewed, shortlisted or rejected." },
      { icon: Users, title: "Shortlists with notes", body: "Save standouts and say why." },
      { icon: MessageSquare, title: "Messaging", body: "Talk to candidates without leaving Donjo." },
      { icon: FileText, title: "Dossier (PDF)", body: "Share applicants and video links offline." },
    ],
    workflowTitle: "From open role to shortlist",
    workflow: [
      { title: "Set up", body: "Create your employer profile." },
      { title: "Post", body: "Describe the role and add a video prompt." },
      { title: "Watch", body: "Review clips as applications arrive." },
      { title: "Shortlist", body: "Pick, message and share a dossier." },
    ],
    audienceTitle: "Who it fits",
    audience: [
      { title: "First-time hiring founders", body: "You are recruiter and CEO." },
      { title: "Studios and agencies", body: "Work is easier to judge on video." },
      { title: "Growing Kenyan SMEs", body: "Practical skill beats paperwork." },
    ],
    faq: [
      { q: "Must candidates record a video?", a: "Where you set a video prompt, yes. They can record in the browser or upload a clip." },
      { q: "Can I still read written applications?", a: "Yes. Applicants add a cover message, bio and skills too." },
      { q: "Is there a free plan?", a: "Yes. Starter is free and includes jobs with a video question, applicant video portfolios, shortlisting, the applicant dossier with ratings, and PDF or CSV export. Plans differ in how many jobs, challenges and shortlist places you can keep active." },
      { q: "How do I contact candidates?", a: "Once you shortlist someone you can message them inside Donjo." },
      { q: "Are candidates told about my decision?", a: "Yes. When you shortlist or reject an application, the applicant gets a notification in the app." },
      { q: "Do I see who has applied to which job?", a: "Yes. Each job has its own applicant list with video portfolios, cover messages and a status for every applicant." },
    ],
    related: ["/platform/video-proof", "/platform/dossier-generation", "/solutions/accelerators"],
  },

  {
    slug: "hackathons",
    family: "solutions",
    icon: Zap,
    name: "Hackathons",
    keyword: "hackathon hiring software",
    summary: "Video submissions, judging and profiles that outlast the weekend.",
    metaTitle: "Hackathon Hiring Software | Donjo",
    metaDescription:
      "Hackathon hiring software with video submissions, winner marking and lasting participant profiles that sponsors can act on. Start free.",
    eyebrow: "Solution",
    headline: "Judge the demo. Keep the talent.",
    subhead: "Hackathon hiring software where every entry is a video, the organiser picks the winner, and participants keep a portfolio.",
    glance: {
      title: "You get",
      items: ["Challenges with prize and deadline", "One video entry per participant", "You choose the winner"],
    },
    problem: {
      title: "The event ends and the signal vanishes",
      points: ["Entries scattered across forms and drives", "Pitches are compared from memory", "Sponsors leave with names, not evidence"],
    },
    help: {
      title: "A challenge that leaves a record",
      intro: "One brief, one video format, and profiles that stay useful afterwards.",
      pillars: [
        { title: "One brief", body: "Prompt, prize, deadline and skills tags." },
        { title: "Clear outcome", body: "Mark the winning entry. Every entry stays on record." },
        { title: "Lasting talent", body: "Sponsors find and shortlist participants." },
      ],
    },
    capabilitiesTitle: "Run a challenge end to end",
    capabilities: [
      { icon: Trophy, title: "Challenge pages", body: "Prize, deadline and skills tags." },
      { icon: Video, title: "Video submissions", body: "One entry per participant." },
      { icon: Gavel, title: "Winner marking", body: "The organiser marks the winning entry." },
      { icon: Users, title: "Participant profiles", body: "Every entry sits on a profile sponsors can shortlist." },
      { icon: Tags, title: "Skills tags", body: "Tag a challenge with the skills it tests." },
      { icon: Bell, title: "Notifications", body: "Updates appear in the app." },
    ],
    workflowTitle: "Run a hackathon on Donjo",
    workflow: [
      { title: "Define", body: "Write the brief and prompt." },
      { title: "Collect", body: "Participants submit a video." },
      { title: "Decide", body: "Watch the entries and pick the winner." },
      { title: "Connect", body: "Sponsors shortlist participants." },
    ],
    audienceTitle: "Who it fits",
    audience: [
      { title: "Organisers", body: "Hubs and companies running build weekends." },
      { title: "Sponsors", body: "Teams hiring from events." },
      { title: "Competition hosts", body: "Pitch and skills contests." },
    ],
    faq: [
      { q: "Does Donjo score teams automatically?", a: "No. The organiser watches the entries and marks the winner. Nothing is scored by software." },
      { q: "Is there a judging panel with scorecards?", a: "Not for challenge entries. The account that posted the challenge reviews the entries and marks the winner. Donjo has a judge account type, but it is for viewing venture applications and pitch decks, not for scoring challenge entries." },
      { q: "Can we run several challenges?", a: "Yes. Each has its own brief, prize, deadline and submissions." },
      { q: "How do entries work?", a: "Each participant submits one video per challenge, recorded in the browser or uploaded." },
      { q: "Do participants need accounts?", a: "Yes. A profile turns one entry into a lasting portfolio." },
      { q: "Can sponsors hire from the event?", a: "Sponsors with an employer account can shortlist and message participants they want to talk to." },
      { q: "Does Donjo handle prize money?", a: "No. Donjo records the prize on the challenge page. It does not collect or pay out money." },
    ],
    related: ["/platform/video-proof", "/solutions/accelerators", "/platform/venture-velocity"],
  },

  {
    slug: "accelerators",
    family: "solutions",
    icon: Rocket,
    name: "Accelerators",
    keyword: "accelerator application software",
    summary: "Structured venture applications and one review queue.",
    metaTitle: "Accelerator Application Software | Donjo",
    metaDescription:
      "Accelerator application software with a guided venture wizard, pitch video, and a shortlist or reject review queue. Start free on Donjo.",
    eyebrow: "Solution",
    headline: "Select cohorts on evidence, not polish.",
    subhead: "Accelerator application software: every venture applies the same way, with a pitch video, into one review queue.",
    glance: {
      title: "You get",
      items: ["Six-step venture application", "Pitch video and deck versions", "Shortlist or reject queue"],
    },
    problem: {
      title: "Selection is slow and uneven",
      points: ["Applications arrive in mixed formats", "Later files get less attention", "Committee packs are assembled by hand"],
    },
    help: {
      title: "One structure, one queue, one record",
      intro: "Founders use the same wizard. Reviewers work one queue.",
      pillars: [
        { title: "Consistent", body: "Same fields for every venture." },
        { title: "Decisive", body: "Shortlist or reject, founders notified." },
        { title: "Exportable", body: "A PDF summary for the committee." },
      ],
    },
    capabilitiesTitle: "Tools for selection",
    capabilities: [
      { icon: Presentation, title: "Venture wizard", body: "Basics to pitch in six guided steps." },
      { icon: Video, title: "Pitch video and decks", body: "Current material always attached." },
      { icon: ClipboardCheck, title: "Review queue", body: "Shortlist or reject with founder notice." },
      { icon: FileText, title: "Dossier export", body: "PDF summary for committees." },
      { icon: Timer, title: "Time-to-decision", body: "How long reviews take, in the admin console." },
      { icon: MapPin, title: "County map", body: "Where ventures and applicants are, in the admin console." },
    ],
    workflowTitle: "From open call to cohort",
    workflow: [
      { title: "Open the call", body: "Founders start the wizard." },
      { title: "Receive", body: "Complete applications with pitch video." },
      { title: "Review", body: "Work the queue, decide." },
      { title: "Export", body: "Take a PDF to committee." },
    ],
    audienceTitle: "Who it fits",
    audience: [
      { title: "Accelerators and incubators", body: "Regular selection rounds." },
      { title: "Venture studios", body: "Many early ideas to compare." },
      { title: "Investors and mentors", body: "Bookmark, pass or highlight ventures." },
    ],
    faq: [
      { q: "What does a founder fill in?", a: "Six guided steps: basics, problem, team, tech, pitch and review. They attach a pitch video and can upload deck versions." },
      { q: "How are founders told about decisions?", a: "When a reviewer shortlists or rejects a venture, the founder is notified." },
      { q: "Is the county map live?", a: "Yes, in the admin console. It shows where ventures and applicants are, from the county each entered. People who did not set a location are counted separately." },
      { q: "Can several staff review?", a: "Reviewing happens in admin accounts, which our team sets up for you. Tell us how many reviewers you need." },
      { q: "Can investors and mentors see applications?", a: "Investor and judge accounts, assigned by admins, can view ventures and pitch decks. Investors can bookmark, pass or highlight a venture." },
      { q: "Can we tag applicants by intake or cohort?", a: "Not yet in the forms. Ventures do not have an intake or cohort field that founders or reviewers can fill in today." },
    ],
    related: ["/platform/venture-velocity", "/platform/dossier-generation", "/solutions/hackathons"],
  },

  {
    slug: "universities",
    family: "solutions",
    icon: GraduationCap,
    name: "Universities",
    keyword: "student video portfolio",
    summary: "Turn coursework into a video portfolio employers can watch.",
    metaTitle: "Student Video Portfolio for Universities | Donjo",
    metaDescription:
      "Give students a video portfolio of real project work and a public profile employers can find. Universities and training providers: request access to Donjo.",
    eyebrow: "Solution",
    headline: "Graduate with proof, not just a transcript.",
    subhead: "A student video portfolio turns projects into evidence that employers can watch and shortlist.",
    glance: {
      title: "You get",
      items: ["Video portfolios and skills", "Shareable public profiles", "Employer discovery"],
    },
    problem: {
      title: "Graduates look alike on paper",
      points: ["Transcripts and CVs blur together", "Project work stays hidden in folders", "Students without networks have no other signal"],
    },
    help: {
      title: "Give every student a way to show work",
      intro: "Students post short project videos. Employers browse, shortlist and message.",
      pillars: [
        { title: "Portfolio", body: "A clip beats a bullet point." },
        { title: "Shareable", body: "A public link for applications." },
        { title: "Real briefs", body: "Enter employer challenges." },
      ],
    },
    capabilitiesTitle: "What students can do today",
    capabilities: [
      { icon: Video, title: "Record or upload", body: "Public or private videos." },
      { icon: Tags, title: "Skills and categories", body: "Make work findable." },
      { icon: Share2, title: "Public profile", body: "Bio, links and videos." },
      { icon: Trophy, title: "Challenges", body: "Submit to live employer briefs." },
      { icon: Eye, title: "Employer discovery", body: "Browse, shortlist and message." },
      { icon: MessageSquare, title: "Messaging", body: "Employers can message students they shortlist." },
    ],
    workflowTitle: "From project to opportunity",
    workflow: [
      { title: "Profile", body: "Add bio and skills." },
      { title: "Record", body: "Explain a project you built." },
      { title: "Share", body: "Publish and send the link." },
      { title: "Get found", body: "Employers watch and reach out." },
    ],
    audienceTitle: "Who it fits",
    audience: [
      { title: "Careers offices", body: "A stronger story than transcripts." },
      { title: "Bootcamps", body: "Show graduates' real work." },
      { title: "Student clubs", body: "Record what members build." },
    ],
    faq: [
      { q: "Do students pay?", a: "No. Pricing applies to teams who hire and run programmes." },
      { q: "Can videos be private?", a: "Yes. Students control visibility." },
      { q: "Can a careers office see all its students?", a: "No. There is no institution or cohort view. Each student has their own profile." },
      { q: "Is there skill-progression tracking?", a: "No. Donjo shows the work a student chooses to post, not progress over time." },
      { q: "Can students enter employer challenges?", a: "Yes. They submit one video to a live employer challenge." },
      { q: "Who can see a student's profile?", a: "Public videos, bio and links appear on the public profile. Private videos stay hidden." },
    ],
    related: ["/platform/video-proof", "/solutions/hackathons", "/solutions/hr-for-startups"],
  },

  {
    slug: "enterprise",
    family: "solutions",
    icon: Building2,
    name: "Enterprise",
    keyword: "enterprise video hiring platform",
    summary: "Role-based, evidence-led recruitment at volume.",
    metaTitle: "Enterprise Video Hiring Platform | Donjo",
    metaDescription:
      "Enterprise video hiring with role-based access, employer dashboards, passkey sign-in and dossier export for structured recruitment. Talk to Donjo.",
    eyebrow: "Solution",
    headline: "Proof-based hiring with role-based access.",
    subhead: "An enterprise video hiring platform: one consistent first look, role-based access, passkey or password sign-in.",
    glance: {
      title: "You get",
      items: ["Role-based access", "Employer dashboards", "Passkey sign-in"],
    },
    problem: {
      title: "Volume hiring loses good people",
      points: ["CV triage varies by screener", "Managers see a narrow slice", "Access to candidate data must be controlled"],
    },
    help: {
      title: "Structure, control and evidence",
      intro: "Every applicant answers the same prompt. Access follows roles.",
      pillars: [
        { title: "Consistent", body: "Same evidence for every panel." },
        { title: "Controlled", body: "Six account roles." },
        { title: "Flexible sign-in", body: "Passkeys or passwords." },
      ],
    },
    capabilitiesTitle: "Controls for structured recruitment",
    capabilities: [
      { icon: UserCog, title: "Role-based access", body: "Talent, employer, admin, judge and more." },
      { icon: LayoutDashboard, title: "Employer dashboard", body: "Postings and applicants together." },
      { icon: Briefcase, title: "Structured postings", body: "Shared video prompt per role." },
      { icon: KeyRound, title: "Passkey sign-in", body: "WebAuthn passkeys, added in account settings." },
      { icon: FileText, title: "Dossier export", body: "PDF summary for panels." },
      { icon: ClipboardCheck, title: "Audit log", body: "Admin sign-ins and actions are recorded." },
    ],
    workflowTitle: "Bringing Donjo into your process",
    workflow: [
      { title: "Scope", body: "Pick a pilot role family." },
      { title: "Set roles", body: "Decide who reviews and decides." },
      { title: "Publish", body: "Post roles with a shared prompt." },
      { title: "Review", body: "Panels decide on the same evidence." },
    ],
    audienceTitle: "Who it fits",
    audience: [
      { title: "Talent acquisition", body: "Structured, high-volume hiring." },
      { title: "Graduate programmes", body: "Many similar applications." },
      { title: "Public-sector employers", body: "Documented, fair selection." },
    ],
    faq: [
      { q: "Which certifications do you hold?", a: "We don't list any here. Share your requirements and we'll answer directly." },
      { q: "Do you offer SSO or ATS integration?", a: "No. Donjo does not have single sign-on or applicant-tracking integrations today." },
      { q: "Is passkey login live?", a: "Yes. Users add a passkey in their account settings and can then sign in with it. Passwords remain available." },
      { q: "Can we brand the platform?", a: "No. There is no white-labelling." },
      { q: "How is access controlled?", a: "Every account has one role: talent, employer, founder, investor, judge or admin. Admin and judge roles can only be assigned by an administrator." },
      { q: "Is there an audit trail?", a: "Yes. Admin sign-ins and privileged actions are written to an audit log that admins can read." },
    ],
    related: ["/platform/dossier-generation", "/platform/venture-velocity", "/solutions/hr-for-startups"],
  },
];
